import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { TApiRes } from "@/types/api";
import { TOption } from "@/types/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSharedMasterData } from "@/features/sharing/context/shared-enum-context";
import {
  TDeleteTagOutcome,
  TGetTagsResDto,
  TSaveTagItem,
  TSaveTagsResDto,
  TTagEntityType,
  TValidateDeleteTagResDto,
} from "@/api/services/main/tag/dtos/tag.dto";

export type { TTag, TTagUsingJob, TSaveTagItem, TTagEntityType, TDeleteTagOutcome } from "@/api/services/main/tag/dtos/tag.dto";

/** GET /tags/:entityType — master tag list of an entity type */
export const fetchTagsApi = async (entityType: TTagEntityType): Promise<TGetTagsResDto> => {
  const rs = await clientApi.get<TGetTagsResDto>(`/tags/${entityType}`);
  return rs.data;
};

export const useTags = (entityType: TTagEntityType) => {
  // On shared (no-auth) pages, tags come from the verify response via context — skip the auth-protected API.
  const sharedMasterData = useSharedMasterData();
  const sharedTags = sharedMasterData ? (entityType === "case-match" ? sharedMasterData.caseMatchTags : sharedMasterData.jobTags) : undefined;

  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.JOB.FETCH_TAGS, entityType],
    queryFn: () => fetchTagsApi(entityType),
    enabled: !sharedMasterData,
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  return { data: sharedTags ?? data?.data ?? [], ...rest };
};

// Returns {label, value: id}[] where value is the numeric tag id.
export const useTagOptions = (entityType: TTagEntityType) => {
  const { data, ...rest } = useTags(entityType);
  // tags.id is bigint → kept as string everywhere (jobs.tag_ids stores string ids) to avoid JS 2^53 precision loss.
  const options = useMemo<TOption[]>(() => data.map((t) => ({ label: t.label, value: t.id })), [data]);
  return { data: options, ...rest };
};

/** PUT /tags/:entityType — bulk upsert + reorder */
export const saveTagsApi = async (entityType: TTagEntityType, tags: TSaveTagItem[]): Promise<TSaveTagsResDto> => {
  const rs = await clientApi.put<TSaveTagsResDto>(`/tags/${entityType}`, { tags });
  return rs.data;
};

export const useSaveTags = (entityType: TTagEntityType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tags: TSaveTagItem[]) => saveTagsApi(entityType, tags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB.FETCH_TAGS, entityType] });
    },
  });
};

/**
 * GET /tags/:entityType/:id/validate-delete — jobs using the tag (delete confirmation).
 * `since` (ISO): restrict to jobs tagged after this cutoff — used to paginate the "newly added" list on delete conflict.
 * `until` (ISO): restrict to jobs tagged at/before this cutoff — freezes the top list to the dialog-open snapshot.
 */
export const validateDeleteTagApi = async (
  entityType: TTagEntityType,
  id: number,
  cursor?: number,
  perPage = 100,
  since?: string,
  until?: string,
): Promise<TValidateDeleteTagResDto> => {
  const url = createUrlWithParams(`/tags/${entityType}/${id}/validate-delete`, { cursor, perPage, since, until });
  const rs = await clientApi.get<TValidateDeleteTagResDto>(url);
  return rs.data;
};

/**
 * DELETE /tags/:entityType/:id — soft-delete + cascade.
 * `validatedAt`: cutoff from the confirm dialog. Jobs tagged since are returned (HTTP 200) as a `success: false`
 * outcome carrying the newly-tagged jobs — not an error — so the dialog can list them for review.
 */
export const deleteTagApi = async (entityType: TTagEntityType, id: number, validatedAt: string): Promise<TDeleteTagOutcome> => {
  const url = createUrlWithParams(`/tags/${entityType}/${id}`, { validatedAt });
  const rs = await clientApi.delete<TApiRes<TDeleteTagOutcome>>(url);
  return rs.data.data;
};

export const useDeleteTag = (entityType: TTagEntityType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, validatedAt }: { id: number; validatedAt: string }) => deleteTagApi(entityType, id, validatedAt),
    onSuccess: (outcome) => {
      // Only a real deletion changes the tag master; a conflict outcome leaves it untouched.
      if (outcome.success) queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB.FETCH_TAGS, entityType] });
    },
  });
};
